/**
 * Document categories service (Story SCH-2.5).
 *
 * Reads the `document_categories` table directly via Supabase (RLS handles
 * the visibility rule: globals + this tenant's rows). Mirrors the
 * `addersService.listAdders` pattern.
 *
 * Inline-create flows (used by SCH Document Filters tab) go through the
 * SCH backend's `/document-filters/with-new-category` endpoint, NOT this
 * service — that endpoint atomically creates the category AND maps it to
 * an SCH filter row in one operation. This service is read-only for now.
 */

import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';

export interface DocumentCategory {
  id: number;
  tenant_id: string | null;
  name: string;
  display_label: string;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const documentCategoriesService = {
  /**
   * List active document categories visible to the current tenant
   * (globals + tenant-specific rows). Sorted by sort_order then
   * display_label for stable display.
   */
  async listDocumentCategories(): Promise<DocumentCategory[]> {
    const tenantId = await getCurrentTenantId();
    if (!tenantId) throw new Error('No tenant ID found');

    const { data, error } = await supabase
      .from('document_categories')
      .select('*')
      .or(`tenant_id.is.null,tenant_id.eq.${tenantId}`)
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .order('display_label', { ascending: true });

    if (error) {
      console.error('Failed to list document categories:', error);
      throw new Error('Unable to load document categories.');
    }
    return (data || []) as DocumentCategory[];
  },
};
